import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import axios from "axios";
import { X, Download, ChevronLeft, ChevronRight, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NATIVE_BROWSER_EXT = new Set([
  "jpg", "jpeg", "png", "webp", "gif", "bmp",
]);

// Every extension the viewer can render — either directly in the
// browser (NATIVE_BROWSER_EXT) or via the /preview transcode endpoint
// (RAW / HEIC / TIFF). Anything else (XMP sidecars, .xml, .txt, etc.)
// is hidden so the grid doesn't fill with "Preview unavailable" tiles.
const VIEWABLE_EXT = new Set([
  "jpg", "jpeg", "png", "webp", "gif", "bmp",
  "heic", "heif", "tif", "tiff",
  "cr2", "cr3", "nef", "arw", "dng", "orf", "rw2", "raf", "sr2", "pef",
]);

function extOf(name) {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
}

/** Pick the fastest URL to render this file in the browser.
 * - Browser-native formats (jpg/png/…) → CDN direct.
 * - RAW / HEIC with pre-generated sibling → CDN direct to
 *   ``<name>.preview.jpg`` (this is the common case now that the
 *   backend auto-generates previews on upload).
 * - RAW / HEIC without a preview yet → /preview endpoint which
 *   transcodes on demand and 302s to the CDN.
 */
function pickImageUrl(jobId, folder, file) {
  if (!file || !file.filename) return "";
  const ext = extOf(file.filename);
  if (file.cdn_url && NATIVE_BROWSER_EXT.has(ext)) return file.cdn_url;
  // Pre-generated sibling preview — instant, no backend hop.
  if (file.preview_ready && file.preview_cdn_url) return file.preview_cdn_url;
  return `${API}/jobs/${jobId}/files/${encodeURIComponent(file.filename)}/preview?folder=${folder}`;
}

/**
 * Filesystem-style folder viewer. Renders a lazy-loaded grid of
 * thumbnails for every file under ``path`` in a job's folder.
 *
 * Handles 700+ photos:
 * - <img loading="lazy"> — browsers only fetch when scrolled into view.
 * - decoding="async" — off the main thread so scroll stays smooth.
 * - Progressive `srcset` avoided; the /preview endpoint always returns
 *   a print-quality JPEG from R2, and Cloudflare edges cache it.
 * - Click a thumb → full-screen lightbox with keyboard nav.
 */
export default function FolderViewer({ jobId, folder, path, onClose, onAnalyze }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  // Load (and re-load) the file listing. Split into a stable callback
  // so the polling effect below can call it without re-declaring the
  // whole fetch pipeline.
  const loadFiles = useCallback(async () => {
    const { data } = await axios.get(`${API}/jobs/${jobId}/files`, {
      params: { folder },
    });
    const all = data.files || [];
    const prefix = path ? `${path}/` : "";
    const scoped = all.filter((f) => {
      if (path && !f.filename.startsWith(prefix)) return false;
      if (!VIEWABLE_EXT.has(extOf(f.filename))) return false;
      // Hide the sibling ``<name>.preview.jpg`` files that the backend
      // auto-generates — they aren't user-uploaded originals, they're
      // rendered inline against the RAW parent's tile.
      if (f.filename.toLowerCase().endsWith(".preview.jpg")) return false;
      return true;
    });
    scoped.sort((a, b) => a.filename.localeCompare(b.filename));
    return scoped;
  }, [jobId, folder, path]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadFiles()
      .then(async (scoped) => {
        if (cancelled) return;
        setFiles(scoped);
        // Legacy folders (files uploaded before background preview
        // generation shipped) won't have any ``.preview.jpg`` siblings
        // on R2 yet. Kick off a one-shot backfill so the tiles fill
        // in as previews land. Idempotent — the backend's inflight
        // set prevents duplicates, and files that already have a
        // sibling preview are silently skipped.
        const anyLegacy = scoped.some(
          (f) => f.preview_ready === false && f.preview_generating === false,
        );
        if (anyLegacy) {
          try {
            await axios.post(
              `${API}/jobs/${jobId}/files/backfill-previews`,
              null,
              { params: { folder } },
            );
            // Re-fetch so the ``preview_generating`` flag flips true
            // for the queued files.
            const refreshed = await loadFiles();
            if (!cancelled) setFiles(refreshed);
          } catch {
            /* non-fatal — user can retry by reopening the viewer */
          }
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [loadFiles, jobId, folder]);

  // Poll the /files endpoint every 4 s while any RAW / HEIC is still
  // background-transcoding. As soon as ``preview_ready`` flips to true
  // for every file, the interval clears itself. Cheap: one JSON call
  // per 4 s while the modal is open.
  useEffect(() => {
    const stillGenerating = files.some(
      (f) => f.preview_generating && !f.preview_ready,
    );
    if (!stillGenerating) return undefined;
    const id = setInterval(async () => {
      try {
        const scoped = await loadFiles();
        setFiles(scoped);
      } catch {
        /* transient network — try again next tick */
      }
    }, 4000);
    return () => clearInterval(id);
  }, [files, loadFiles]);

  // Keyboard: Esc to close, arrows to navigate lightbox.
  const closeLightbox = useCallback(() => setLightboxIdx(-1), []);
  const nextImage = useCallback(() => {
    setLightboxIdx((i) => (i + 1) % files.length);
  }, [files.length]);
  const prevImage = useCallback(() => {
    setLightboxIdx((i) => (i - 1 + files.length) % files.length);
  }, [files.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (lightboxIdx >= 0) closeLightbox();
        else onClose();
      } else if (lightboxIdx >= 0) {
        if (e.key === "ArrowRight") nextImage();
        else if (e.key === "ArrowLeft") prevImage();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxIdx, closeLightbox, nextImage, prevImage, onClose]);

  const totalSize = useMemo(
    () => files.reduce((n, f) => n + (f.size || 0), 0),
    [files],
  );
  const humanSize = totalSize > 1e9
    ? `${(totalSize / 1e9).toFixed(2)} GB`
    : `${(totalSize / 1e6).toFixed(1)} MB`;

  const generatingCount = useMemo(
    () => files.filter((f) => f.preview_generating && !f.preview_ready).length,
    [files],
  );

  const activeFile = lightboxIdx >= 0 ? files[lightboxIdx] : null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-stretch justify-center"
      data-testid="folder-viewer"
    >
      <div className="bg-white w-full max-w-[1400px] my-6 mx-6 rounded-lg shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-slate-800 truncate" data-testid="viewer-path">
              {path || "/"}
            </h2>
            <p className="text-[11px] text-slate-500 tabular-nums" data-testid="viewer-stats">
              {loading ? "Loading…" : `${files.length} files · ${humanSize}`}
              {!loading && generatingCount > 0 && (
                <span className="ml-2 text-amber-600">
                  · {generatingCount} preview{generatingCount === 1 ? "" : "s"} generating…
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {folder === "uploaded" && onAnalyze && files.length > 0 && !loading && (
              <button
                type="button"
                onClick={async () => {
                  if (generatingCount > 0) {
                    // Analyzing while previews are still being made
                    // means the analyzer's slow path decodes some RAW
                    // files from source. It still works, just uses
                    // more CPU per file — warn but don't block.
                    toast.info(
                      `Starting analysis — ${generatingCount} preview${
                        generatingCount === 1 ? "" : "s"
                      } still generating. First-time analyses of those files will be slower.`,
                    );
                  }
                  try {
                    await onAnalyze(path);
                    toast.success(
                      path
                        ? `Analysis started for '${path}'`
                        : "Analysis started for the whole folder",
                    );
                    onClose();
                  } catch (err) {
                    toast.error(
                      err?.response?.data?.detail || "Failed to start analysis",
                    );
                  }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                data-testid="viewer-analyze-folder"
                title={path ? `Analyze all photos under '${path}'` : "Analyze this whole folder"}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Analyze this folder
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-md hover:bg-slate-200 text-slate-600"
              data-testid="viewer-close"
              title="Close (Esc)"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Grid — browser-native lazy loading. */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading file list…
            </div>
          ) : files.length === 0 ? (
            <p className="text-center text-slate-500 py-12">No files in this folder.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {files.map((f, i) => (
                <ThumbCard
                  key={f.filename}
                  jobId={jobId}
                  folder={folder}
                  file={f}
                  onOpen={() => setLightboxIdx(i)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {activeFile && (
        <div
          className="absolute inset-0 z-[110] bg-black/90 flex flex-col"
          onClick={closeLightbox}
          data-testid="viewer-lightbox"
        >
          <div className="flex items-center justify-between px-5 py-3 text-slate-200 text-sm">
            <div className="min-w-0 tabular-nums">
              <span className="font-bold">{lightboxIdx + 1} / {files.length}</span>
              <span className="mx-2 text-slate-500">·</span>
              <span className="truncate">{activeFile.filename}</span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={`${API}/jobs/${jobId}/files/${encodeURIComponent(activeFile.filename)}/download?folder=${folder}`}
                onClick={(e) => e.stopPropagation()}
                className="p-2 rounded-md hover:bg-white/10"
                title="Download original"
              >
                <Download className="h-4 w-4" />
              </a>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                className="p-2 rounded-md hover:bg-white/10"
                data-testid="lightbox-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div
            className="flex-1 flex items-center justify-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <img
              src={pickImageUrl(jobId, folder, activeFile)}
              alt={activeFile.filename}
              className="max-w-[90vw] max-h-[80vh] object-contain"
              decoding="async"
              data-testid="lightbox-image"
            />
            <button
              type="button"
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
              data-testid="lightbox-next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ThumbCard({ jobId, folder, file, onOpen }) {
  const url = pickImageUrl(jobId, folder, file);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const shortName = useMemo(() => {
    const parts = file.filename.split("/");
    return parts[parts.length - 1];
  }, [file.filename]);

  // Is this a RAW / HEIC still waiting for its background transcode?
  // In that case we skip the <img> entirely (loading it would kick off
  // an on-demand transcode that can take 30 s and time the browser out)
  // and render a "Generating preview…" placeholder that the parent
  // component will replace once the polling interval refreshes the
  // ``preview_ready`` flag.
  const isGenerating = file.preview_generating && !file.preview_ready;

  return (
    <button
      type="button"
      onClick={isGenerating ? undefined : onOpen}
      disabled={isGenerating}
      className="relative aspect-square rounded-md overflow-hidden bg-slate-100 border border-slate-200 hover:border-primary hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-wait disabled:hover:border-slate-200 disabled:hover:shadow-none"
      data-testid={`viewer-thumb-${file.filename}`}
    >
      {isGenerating ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-[10px] text-slate-500 p-2 text-center gap-2">
          <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
          <span>Generating preview…</span>
        </div>
      ) : !errored ? (
        <img
          src={url}
          alt={shortName}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400 p-2 text-center">
          Preview unavailable
        </div>
      )}
      {!isGenerating && !loaded && !errored && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <p className="text-[10px] text-white font-semibold truncate">{shortName}</p>
      </div>
    </button>
  );
}
